import { PlusIcon } from 'lucide-react'
import { useCallback, useState } from 'react'

import { useDisconnectCreatedIssue, useGetJiraCreatedIssues } from '@/api/jira.hook'

import { useTrackCallback } from '@/lib/analytics/events'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { JiraCreateIssueModal } from '@/pages/assessment/components/create-jira-issue-modal/jira-create-issue-modal'

import { Button } from '@/components/lemmala-design-system/button/button'
import { SplitButtons } from '@/components/lemmala-design-system/button/split-buttons'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import JiraIcon from '@/assets/jira.svg'

interface JiraAssessmentIntegrationProps {
  assessmentId: string
  companyId: string
}

export function JiraAssessmentIntegration({
  assessmentId,
  companyId,
}: JiraAssessmentIntegrationProps) {
  const trackingContext = useAssessmentAnalytics()
  const trackCreateJiraTicketClick = useTrackCallback(
    'third-party.assessment.jira-ticket.create.click',
  )
  const { data: createdJiraIssues, isLoading: isCreatedIssuesLoading } = useGetJiraCreatedIssues({
    orgCompanyId: companyId,
    enabled: !!companyId,
  })

  const assessmentCreatedJiraIssue = createdJiraIssues?.issues?.find(
    (issue) => issue.assessmentId === assessmentId && !issue.subtask,
  )

  const [isJiraCreateIssueModalOpen, setIsJiraCreateIssueModalOpen] = useState(false)
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false)

  const { mutateAsync: disconnectJiraIssue, isPending: isDisconnectPending } =
    useDisconnectCreatedIssue()

  const handleJiraIssueCreated = useCallback(
    (_response: { id: string; key: string; link: string }) => {
      setIsJiraCreateIssueModalOpen(false)
      // Could add toast notification here if needed
    },
    [],
  )

  const handleDisconnectJiraIssue = useCallback(async () => {
    if (!assessmentCreatedJiraIssue) {
      return
    }

    try {
      await disconnectJiraIssue({
        orgCompanyId: companyId,
        assessmentId: assessmentCreatedJiraIssue.assessmentId,
        issueKey: assessmentCreatedJiraIssue.issueKey,
      })
      setIsDisconnectModalOpen(false)
      // Could add toast notification here if needed
    } catch (error) {
      console.error('Failed to disconnect Jira issue:', error)
      // Could add error toast notification here
    }
  }, [assessmentCreatedJiraIssue, disconnectJiraIssue, companyId])

  if (isCreatedIssuesLoading) {
    return null
  }

  return (
    <>
      {assessmentCreatedJiraIssue ? (
        <SplitButtons
          variant='secondary'
          aria-label={assessmentCreatedJiraIssue.issueKey}
          secondaryAriaLabel={`Disconnect ${assessmentCreatedJiraIssue.issueKey}`}
          onPrimaryClick={() =>
            window.open(`https://${assessmentCreatedJiraIssue.issueLink}`, '_blank')
          }
          onSecondaryClick={() => setIsDisconnectModalOpen(true)}
        >
          <TextWithIcon
            icon={<JiraIcon width={16} height={16} />}
            text={assessmentCreatedJiraIssue.issueKey}
          />
        </SplitButtons>
      ) : (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          aria-label='Create Jira ticket'
          onClick={() => {
            if (trackingContext) {
              trackCreateJiraTicketClick(trackingContext)
            }

            setIsJiraCreateIssueModalOpen(true)
          }}
        >
          <TextWithIcon icon={<PlusIcon width={16} height={16} />} text={'Create Jira ticket'} />
        </Button>
      )}

      {isJiraCreateIssueModalOpen && (
        <JiraCreateIssueModal
          companyId={companyId}
          assessmentId={assessmentId}
          onFinish={handleJiraIssueCreated}
          onCancel={() => setIsJiraCreateIssueModalOpen(false)}
        />
      )}

      {assessmentCreatedJiraIssue && (
        <Modal
          variant='destructive'
          title='Disconnect Jira ticket'
          primaryButtonText='Disconnect'
          open={isDisconnectModalOpen}
          onOk={handleDisconnectJiraIssue}
          onCancel={() => setIsDisconnectModalOpen(false)}
          confirmLoading={isDisconnectPending}
        >
          This will remove the link between this assessment and{' '}
          {assessmentCreatedJiraIssue.issueKey}. The ticket will not be deleted in Jira.
        </Modal>
      )}
    </>
  )
}
